import i18n from '@/i18n';
import type { AuditLogEntry } from '@/types/api';

/**
 * Server actions emit one of these action codes; the human-friendly
 * label is what the audit log surfaces in the UI. Unknown actions fall
 * back to the raw code so the journal never silently drops entries.
 */
export function formatAuditAction(action: string): string {
  const labels: Record<string, string> = {
    MEMBER_KICK: i18n.t('srv2.audit.memberKick'),
    MEMBER_BAN: i18n.t('srv2.audit.memberBan'),
    MEMBER_UNBAN: i18n.t('srv2.audit.memberUnban'),
    MEMBER_TIMEOUT: i18n.t('srv2.audit.memberTimeout'),
    MEMBER_TIMEOUT_CLEAR: i18n.t('srv2.audit.memberTimeoutClear'),
    MEMBER_ROLES_UPDATE: i18n.t('srv2.audit.memberRolesUpdate'),
    GUILD_UPDATE: i18n.t('srv2.audit.guildUpdate'),
    ROLE_CREATE: i18n.t('srv2.audit.roleCreate'),
    ROLE_UPDATE: i18n.t('srv2.audit.roleUpdate'),
    ROLE_DELETE: i18n.t('srv2.audit.roleDelete'),
    ROLE_REORDER: i18n.t('srv2.audit.roleReorder'),
    CHANNEL_CREATE: i18n.t('srv2.audit.channelCreate'),
    CHANNEL_UPDATE: i18n.t('srv2.audit.channelUpdate'),
    CHANNEL_DELETE: i18n.t('srv2.audit.channelDelete'),
    CHANNEL_REORDER: i18n.t('srv2.audit.channelReorder'),
    CHANNEL_OVERRIDE_UPSERT: i18n.t('srv2.audit.channelOverrideUpsert'),
    CHANNEL_OVERRIDE_DELETE: i18n.t('srv2.audit.channelOverrideDelete'),
  };
  return labels[action] ?? action;
}

/**
 * Pull a human-readable name out of an audit log entry for actions that
 * don't have a `targetUserId` (role / channel / etc). The server stores
 * `serializeRole(role)` / similar payloads in `metadata`, so for most
 * mutation actions a `.name` is right there. For UPDATE actions we
 * prefer the "after" snapshot since that's the post-change state.
 */
export function extractTargetLabel(log: AuditLogEntry): string | null {
  const meta = log.metadata as Record<string, unknown> | null | undefined;
  if (!meta || typeof meta !== 'object') return null;
  const fromName = (value: unknown): string | null =>
    value && typeof value === 'object' && 'name' in value && typeof (value as { name: unknown }).name === 'string'
      ? ((value as { name: string }).name)
      : null;
  // Direct name on metadata (CREATE / DELETE serialize the whole object).
  if (typeof (meta as { name?: unknown }).name === 'string') {
    return (meta as { name: string }).name;
  }
  // UPDATE actions store { before, after } — show the new name.
  const after = fromName(meta.after);
  if (after) return after;
  const before = fromName(meta.before);
  if (before) return before;
  return null;
}

/** Locale-aware short date-time used by audit log + bans / invites lists. */
export function formatDateTime(value: string): string {
  return new Intl.DateTimeFormat('ru-RU', {
    dateStyle: 'short',
    timeStyle: 'short',
  }).format(new Date(value));
}
